import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Image,
  Space,
  Tag,
  Select,
  Upload,
  Row,
  Col,
  Statistic,
  Segmented,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined,
  ArrowUpOutlined, ArrowDownOutlined, SortAscendingOutlined,
  DownloadOutlined, ImportOutlined,
} from '@ant-design/icons';
import {
  fetchProductList,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadProductImage,
  updateProductSort,
  downloadImportTemplate,
  importProducts,
  batchUpdateProductStatus,
} from '../api/product';
import { fetchCategoryList } from '../api/category';
import { fetchSubcategoriesByParent } from '../api/subcategory';
import { getImageUrl } from '../utils/image';

const { TextArea } = Input;

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingDetailImage, setUploadingDetailImage] = useState(false);
  const [statusView, setStatusView] = useState('全部');
  const [merchGroup, setMerchGroup] = useState('全部');
  const [productImages, setProductImages] = useState([]);
  const [productDetailImages, setProductDetailImages] = useState([]);
  const [sortMode, setSortMode] = useState(false);
  const [sortOrderMap, setSortOrderMap] = useState({});
  const [sortSaving, setSortSaving] = useState(false);
  const [dragProductId, setDragProductId] = useState(null);
  const [importVisible, setImportVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);
  const [form] = Form.useForm();

  // 下载导入模板
  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadImportTemplate();
      const blob = res?.data || res;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '商品导入模板.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('下载模板失败');
    }
  };

  // 批量导入
  const handleImport = async ({ file }) => {
    setImporting(true);
    setImportResult(null);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await importProducts(data);
      if (res?.success) {
        setImportResult(res.data || { total: 0, success: 0, failed: [] });
        message.success(res.message || '导入完成');
        loadProducts(pagination.current, pagination.pageSize);
      } else {
        message.error(res?.message || '导入失败');
      }
    } catch (error) {
      message.error(error?.appMessage || '导入失败');
    } finally {
      setImporting(false);
    }
  };

  // 批量上下架
  const handleBatchStatus = async (status) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先勾选商品');
      return;
    }
    setBulkStatusLoading(true);
    try {
      const res = await batchUpdateProductStatus(selectedRowKeys, status);
      if (res?.success) {
        message.success(res.message || '批量操作成功');
        setSelectedRowKeys([]);
        loadProducts(pagination.current, pagination.pageSize);
      } else {
        message.error(res?.message || '批量操作失败');
      }
    } catch (error) {
      message.error('批量操作失败');
    } finally {
      setBulkStatusLoading(false);
    }
  };

  const buildListParams = (page = 1, pageSize = 10, merch = merchGroup, status = statusView) => {
    const p = { page, pageSize };
    if (status !== '全部') {
      const map = { 在售: 1, 已下架: 0, 低库存: undefined, 已售罄: undefined };
      if (map[status] !== undefined) p.status = map[status];
      // 低库存/已售罄为前端过滤
    }
    if (merch === '新品') p.is_new = 1;
    else if (merch === '热门') p.is_hot = 1;
    else if (merch === '推荐') p.is_recommend = 1;
    else if (merch === '折扣') p.has_discount = 1;
    return p;
  };

  const loadProducts = async (page = 1, pageSize = 10, merch = merchGroup, status = statusView) => {
    setLoading(true);
    try {
      const res = await fetchProductList(buildListParams(page, pageSize, merch, status));
      if (res?.success) {
        const list = res.data?.list || [];
        const total = res.data?.pagination?.total ?? list.length;
        setProducts(list);
        setPagination({ current: page, pageSize, total });
      } else {
        message.error(res?.message || '获取商品列表失败');
      }
    } catch (error) {
      message.error('获取商品列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts(1, 10, merchGroup, statusView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchGroup, statusView]);

  useEffect(() => {
    fetchCategoryList({ page: 1, pageSize: 200 }).then((res) => {
      setCategories(res?.data?.list || res?.data?.data?.list || []);
    }).catch(() => message.error('获取商品分类失败'));
  }, []);

  // 进入/退出排序模式时初始化排序值映射
  const enterSortMode = () => {
    setSortOrderMap(
      products.reduce((acc, item) => {
        acc[item.id] = Number(item.sort_order || 0);
        return acc;
      }, {})
    );
    setSortMode(true);
  };

  const exitSortMode = () => {
    setSortMode(false);
    setSortOrderMap({});
  };

  // 上移/下移交换当前可见商品顺序
  const moveProduct = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= visibleProducts.length) return;
    const next = [...visibleProducts];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    const idMap = new Map(products.map((p) => [p.id, p]));
    const reordered = next.map((p) => idMap.get(p.id));
    setProducts(reordered);
  };

  const handleGridDragStart = (id) => setDragProductId(id);
  const handleGridDrop = (targetId) => {
    if (dragProductId == null || dragProductId === targetId) { setDragProductId(null); return; }
    const fromIdx = visibleProducts.findIndex(p => Number(p.id) === Number(dragProductId));
    const toIdx = visibleProducts.findIndex(p => Number(p.id) === Number(targetId));
    if (fromIdx < 0 || toIdx < 0) { setDragProductId(null); return; }
    const nextVis = [...visibleProducts];
    const [mv] = nextVis.splice(fromIdx, 1);
    nextVis.splice(toIdx, 0, mv);
    // 同步到 products（当前页即全量，保留全局顺序）
    const idMap = new Map(products.map(p => [p.id, p]));
    const newProducts = nextVis.map(p => idMap.get(p.id));
    setProducts(newProducts);
    setDragProductId(null);
  };

  const handleGridSaveSortFromView = async () => {
    if (!visibleProducts.length) return;
    const items = visibleProducts.map((item, index) => ({ id: item.id, sort_order: index + 1 }));
    // 若在分组内，加入组前缀避免与其他组排序冲突：后端按传入顺序写入即可
    setSortSaving(true);
    try {
      const res = await updateProductSort(items);
      if (res?.success) {
        message.success('排序已保存');
        setSortMode(false);
        setSortOrderMap({});
        loadProducts(pagination.current, pagination.pageSize, merchGroup, statusView);
      } else message.error(res?.message || '保存失败');
    } catch (e) { message.error(e?.msg || e?.message || '保存失败'); }
    setSortSaving(false);
  };

  const handleSaveSort = async () => {
    if (!Object.keys(sortOrderMap).length && !visibleProducts.length) {
      message.warning('没有需要保存的排序');
      return;
    }
    setSortSaving(true);
    try {
      const items = visibleProducts.map((item, index) => ({
        id: item.id,
        sort_order: index + 1,
      }));
      const res = await updateProductSort(items);
      if (res?.success) {
        message.success('排序已保存');
        setSortMode(false);
        setSortOrderMap({});
        loadProducts(pagination.current, pagination.pageSize, merchGroup, statusView);
      } else {
        message.error(res?.message || '保存排序失败');
      }
    } catch (error) {
      message.error('保存排序失败');
    } finally {
      setSortSaving(false);
    }
  };

  const loadSubcategories = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    try {
      const res = await fetchSubcategoriesByParent(categoryId, { page: 1, pageSize: 200 });
      setSubcategories(res?.data?.list || res?.data || []);
    } catch (error) {
      setSubcategories([]);
      message.error('获取二级分类失败');
    }
  };

  // 上传前压缩大图，避免体积过大导致 413
  const compressImage = (file) => new Promise((resolve, reject) => {
    if (!file.type?.startsWith('image/') || file.size <= 1024 * 1024) {
      resolve(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX_WIDTH = 1600;
      const MAX_HEIGHT = 1600;
      let { width, height } = img;
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(file);
          return;
        }
        const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
        resolve(new File([blob], `${baseName}.jpg`, { type: blob.type || 'image/jpeg' }));
      }, 'image/jpeg', 0.85);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`「${file.name}」解析失败`));
    };
    img.src = url;
  });

  const handleImagesUpload = async (fileList) => {
    let files = [];
    for (const file of fileList) {
      if (!file.type?.startsWith('image/')) {
        message.error(`「${file.name}」不是图片文件，已跳过`);
        continue;
      }
      try {
        const compressed = await compressImage(file);
        if (compressed.size > 10 * 1024 * 1024) {
          message.error(`「${file.name}」压缩后仍超过 10MB，已跳过`);
          continue;
        }
        files.push(compressed);
      } catch (error) {
        message.error(`「${file.name}」处理失败，请重试`);
      }
    }
    if (!files.length) return;

    const remaining = 8 - productImages.length;
    if (remaining <= 0) {
      message.warning('最多上传 8 张商品图片');
      return;
    }
    files = files.slice(0, remaining);
    if (fileList.length > remaining) {
      message.warning(`最多上传 8 张，本次只上传前 ${remaining} 张`);
    }

    // 一次请求总大小上限（保留 multipart 头部余量）
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    if (totalBytes > 80 * 1024 * 1024) {
      message.error('本次选择的图片总大小超过 80MB，请减少张数后重试');
      return;
    }

    setUploadingImage(true);
    try {
      const data = new FormData();
      files.forEach(file => data.append('file', file));
      const res = await uploadProductImage(data);
      const urls = res?.data?.urls || (res?.data?.url ? [res.data.url] : []);
      if (!urls.length) throw new Error(res?.message || res?.msg || '上传失败');
      setProductImages(images => [...images, ...urls]);
      message.success(`成功上传 ${urls.length} 张图片`);
    } catch (error) {
      message.error(error?.code === 'ECONNABORTED' ? '上传时间过长，请检查网络后重试' : (error?.message || '图片上传失败'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleBeforeUpload = (file, fileList) => {
    handleImagesUpload(fileList);
    return Upload.LIST_IGNORE;
  };

  // 详情长图上传：不压缩、不限定张数与宽高比，保持原图
  const handleDetailImagesUpload = async (fileList) => {
    const files = [];
    for (const file of fileList) {
      if (!file.type?.startsWith('image/')) {
        message.error(`「${file.name}」不是图片文件，已跳过`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        message.error(`「${file.name}」超过 10MB，已跳过`);
        continue;
      }
      files.push(file);
    }
    if (!files.length) return;

    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    if (totalBytes > 80 * 1024 * 1024) {
      message.error('本次选择的图片总大小超过 80MB，请减少张数后重试');
      return;
    }

    setUploadingDetailImage(true);
    try {
      const data = new FormData();
      files.forEach(file => data.append('file', file));
      const res = await uploadProductImage(data);
      const urls = res?.data?.urls || (res?.data?.url ? [res.data.url] : []);
      if (!urls.length) throw new Error(res?.message || res?.msg || '上传失败');
      setProductDetailImages(images => [...images, ...urls]);
      message.success(`成功上传 ${urls.length} 张详情图`);
    } catch (error) {
      message.error(error?.code === 'ECONNABORTED' ? '上传时间过长，请检查网络后重试' : (error?.message || '详情图上传失败'));
    } finally {
      setUploadingDetailImage(false);
    }
  };

  const handleDetailBeforeUpload = (file, fileList) => {
    handleDetailImagesUpload(fileList);
    return Upload.LIST_IGNORE;
  };

  const moveDetailImage = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= productDetailImages.length) return;
    setProductDetailImages(images => {
      const next = [...images];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const moveImage = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= productImages.length) return;
    setProductImages(images => {
      const next = [...images];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const openModal = (record = null) => {
    setEditingProduct(record);
    setModalVisible(true);
    if (record) {
      let images = [];
      try {
        images = Array.isArray(record.images) ? record.images : JSON.parse(record.images || '[]');
      } catch (error) {
        images = [];
      }
      const coverImage = record.main_image || record.image_url;
      setProductImages(images.length ? images : (coverImage ? [coverImage] : []));
      let detailImages = [];
      try {
        const rawDetail = record.detail_images ?? record.detailImages;
        detailImages = Array.isArray(rawDetail) ? rawDetail : JSON.parse(rawDetail || '[]');
        if (!Array.isArray(detailImages)) detailImages = [];
      } catch (error) {
        detailImages = [];
      }
      setProductDetailImages(detailImages.filter(Boolean));
      form.setFieldsValue({
        name: record.name,
        subtitle: record.subtitle,
        description: record.description,
        keywords: record.keywords,
        price: record.price,
        original_price: record.original_price,
        cost_price: record.cost_price,
        stock: record.stock,
        weight_text: record.weight_text ?? (record.weight != null ? `${record.weight}克` : ''),
        unit: record.unit,
        category_id: record.category_id,
        subcategory_id: record.subcategory_id,
        status: record.status === 1,
        is_new: !!record.is_new,
        is_hot: !!record.is_hot,
        is_recommend: !!record.is_recommend,
      });
      loadSubcategories(record.category_id);
    } else {
      setSubcategories([]);
      setProductImages([]);
      setProductDetailImages([]);
      form.resetFields();
      form.setFieldsValue({ status: true, is_new: false, is_hot: false, is_recommend: false, stock: 0, price: 0, unit: '件' });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!productImages.length) {
        message.error('请至少上传一张商品图片');
        return;
      }
      const payload = {
        ...values,
        original_price: values.original_price === '' || values.original_price == null
          ? null
          : values.original_price,
        main_image: productImages[0],
        image_url: productImages[0],
        images: JSON.stringify(productImages),
        detail_images: productDetailImages.length ? JSON.stringify(productDetailImages) : null,
        status: values.status ? 1 : 0,
        is_new: values.is_new ? 1 : 0,
        is_hot: values.is_hot ? 1 : 0,
        is_recommend: values.is_recommend ? 1 : 0,
      };

      if (payload.original_price != null && Number(payload.original_price) < Number(payload.price)) {
        message.error('商品原价不能低于销售价，请修改原价或将其留空');
        return;
      }

      const res = editingProduct
        ? await updateProduct(editingProduct.id, payload)
        : await createProduct(payload);

      if (res?.success) {
        message.success(editingProduct ? '更新成功' : '创建成功');
        setModalVisible(false);
        loadProducts(pagination.current, pagination.pageSize);
      } else {
        message.error(res?.message || '保存失败');
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.appMessage || error?.response?.data?.message || '保存失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteProduct(id);
      if (res?.success) {
        message.success('删除成功');
        loadProducts(pagination.current, pagination.pageSize);
      } else {
        message.error(res?.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const columns = [
    {
      title: '排序',
      key: 'sort',
      width: 110,
      fixed: 'left',
      render: (_, record, index) => sortMode ? (
        <Space size={4}>
          <Button
            size="small"
            type="text"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => moveProduct(index, -1)}
          />
          <Button
            size="small"
            type="text"
            icon={<ArrowDownOutlined />}
            disabled={index === visibleProducts.length - 1}
            onClick={() => moveProduct(index, 1)}
          />
          <span style={{ fontSize: 12, color: '#999', width: 28, textAlign: 'center' }}>
            {sortOrderMap[record.id] != null ? sortOrderMap[record.id] : index + 1}
          </span>
        </Space>
      ) : (
        <span style={{ fontSize: 12, color: '#999' }}>
          {record.sort_order != null ? record.sort_order : index + 1}
        </span>
      ),
    },
    {
      title: '图片',
      dataIndex: 'main_image',
      width: 80,
      render: (_, record) => (
        <Image
          width={48}
          height={48}
          src={getImageUrl(record.main_image || record.image_url)}
          style={{ objectFit: 'cover', borderRadius: 4 }}
        />
      ),
    },
    { title: '名称', dataIndex: 'name', ellipsis: true },
    {
      title: '价格',
      dataIndex: 'price',
      width: 100,
      render: (v) => `¥${v}`,
    },
    { title: '库存', dataIndex: 'stock', width: 80 },
    {
      title: '标签',
      key: 'tags',
      width: 120,
      render: (_, record) => (
        <Space size={4} wrap>
          {record.is_new ? <Tag color="green">新品</Tag> : null}
          {record.is_hot ? <Tag color="red">热门</Tag> : null}
          {record.is_recommend ? <Tag color="blue">推荐</Tag> : null}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'default'}>
          {status === 1 ? '上架' : '下架'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 176,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0} wrap={false}>
          <Button type="link" icon={<EditOutlined />} onClick={() => openModal(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该商品？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const summary = {
    total: pagination.total,
    active: products.filter(item => item.status === 1).length,
    inactive: products.filter(item => item.status === 0).length,
    lowStock: products.filter(item => Number(item.stock) > 0 && Number(item.stock) <= 10).length,
    soldOut: products.filter(item => Number(item.stock) === 0).length,
  };

  const visibleProducts = products.filter(item => {
    if (statusView === '在售') return item.status === 1;
    if (statusView === '已下架') return item.status === 0;
    if (statusView === '低库存') return Number(item.stock) > 0 && Number(item.stock) <= 10;
    if (statusView === '已售罄') return Number(item.stock) === 0;
    return true;
  });

  return (
    <div className="workspace-page">
      <div className="workspace-header">
        <div>
          <h1 className="workspace-title">商品管理</h1>
          <p className="workspace-description">管理商品图片、分类、价格、库存和商城展示状态</p>
        </div>
        <Space>
          {sortMode ? (
            <>
              <Button onClick={exitSortMode}>取消</Button>
              <Button type="primary" loading={sortSaving} icon={<SortAscendingOutlined />} onClick={handleSaveSort}>
                保存排序
              </Button>
            </>
          ) : (
            <Button icon={<SortAscendingOutlined />} onClick={enterSortMode}>
              调整排序
            </Button>
          )}
          <Button icon={<DownloadOutlined />} onClick={() => setImportVisible(true)}>
            批量导入
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            新增商品
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} className="workspace-summary">
        <Col xs={12} md={6}><Card size="small"><Statistic title="全部商品" value={summary.total} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="当前在售" value={summary.active} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="库存不足" value={summary.lowStock} valueStyle={{ color: '#c68a2b' }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="已经售罄" value={summary.soldOut} valueStyle={{ color: '#c4473d' }} /></Card></Col>
      </Row>

      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>分组视图：</span>
        <Segmented
          value={merchGroup}
          onChange={(v) => { setMerchGroup(v); setStatusView('全部'); setSelectedRowKeys([]); }}
          options={['全部', '新品', '热门', '推荐', '折扣']}
        />
        <span style={{ fontSize: 12, color: '#999' }}>· {merchGroup === '全部' ? '全部商品' : merchGroup + '组'} · 拖拽排序所见即所得（左→右、上→下，2列/排）</span>
      </div>

      <Card className="workspace-data-card">
        <div className="workspace-viewbar" style={{ flexWrap: 'wrap', gap: 8 }}>
          <Segmented
            value={statusView}
            onChange={setStatusView}
            options={['全部', '在售', '已下架', '低库存', '已售罄']}
          />
          <Space wrap>
            {selectedRowKeys.length > 0 && (
              <>
                <span style={{ fontSize: 12, color: '#666' }}>已选 {selectedRowKeys.length} 项</span>
                <Popconfirm title="批量上架选中的商品？" onConfirm={() => handleBatchStatus(1)}>
                  <Button size="small" loading={bulkStatusLoading}>批量上架</Button>
                </Popconfirm>
                <Popconfirm title="批量下架选中的商品？" onConfirm={() => handleBatchStatus(0)}>
                  <Button size="small" danger loading={bulkStatusLoading}>批量下架</Button>
                </Popconfirm>
              </>
            )}
            <span className="workspace-result-count">当前页 {visibleProducts.length} 个 / 共 {pagination.total} 个</span>
            <Tag>{merchGroup} · {statusView}</Tag>
            {sortMode && <Tag color="blue">拖拽排序中</Tag>}
          </Space>
        </div>

      {sortMode && (
        <div style={{ marginBottom: 16, padding: 12, background: '#fafcff', border: '1px dashed #d6e4ff', borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>拖拽卡片可调换顺序（显示顺序：先左后右，再上到下 — 第一排为第1、2个，第二排为第3、4个，以此类推），拖完点「保存排序」</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {visibleProducts.map((item) => (
              <div
                key={item.id}
                draggable
                onDragStart={() => handleGridDragStart(item.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleGridDrop(item.id)}
                style={{
                  display: 'flex', gap: 10, alignItems: 'center', padding: 8, background: '#fff', borderRadius: 8,
                  border: dragProductId === item.id ? '2px solid #1677ff' : '1px solid #eee',
                  opacity: dragProductId === item.id ? 0.6 : 1, cursor: 'grab', boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                }}
              >
                <Image width={56} height={56} src={getImageUrl(item.main_image || item.image_url)} style={{ objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} preview={false} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>#{visibleProducts.findIndex(x => x.id === item.id) + 1} · 排序 {item.sort_order ?? '-'} · ¥{item.price} · 库存 {item.stock}</div>
                  <Space size={4} style={{ marginTop: 2 }}>{item.is_new ? <Tag color="green" style={{ fontSize: 11, lineHeight: '16px' }}>新品</Tag> : null}{item.is_hot ? <Tag color="red" style={{ fontSize: 11, lineHeight: '16px' }}>热门</Tag> : null}{item.is_recommend ? <Tag color="blue" style={{ fontSize: 11, lineHeight: '16px' }}>推荐</Tag> : null}</Space>
                </div>
                <span style={{ fontSize: 16, color: '#bbb', userSelect: 'none' }}>⋮⋮</span>
              </div>
            ))}
            {!visibleProducts.length && <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#999', padding: 24 }}>当前分组无商品</div>}
          </div>
        </div>
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={visibleProducts}
        loading={loading}
        rowSelection={sortMode ? undefined : {
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        onRow={(record) => sortMode ? {
          draggable: true,
          onDragStart: () => handleGridDragStart(record.id),
          onDragOver: (e) => e.preventDefault(),
          onDrop: () => handleGridDrop(record.id),
          style: { background: dragProductId === record.id ? '#e6f4ff' : undefined, cursor: sortMode ? 'grab' : undefined }
        } : {}}
        scroll={{ x: 1000 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => loadProducts(page, pageSize, merchGroup, statusView),
        }}
      />

      <Modal
        title={editingProduct ? '编辑商品' : '新增商品'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={760}
        styles={{ body: { maxHeight: '68vh', overflowY: 'auto', paddingRight: 8 } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="商品名称" rules={[{ required: true, message: '请输入名称' }, { max: 200 }]}>
            <Input placeholder="例如：2026福鼎高山寿眉礼盒" />
          </Form.Item>
          <Form.Item name="subtitle" label="商品短标题" rules={[{ max: 200, message: '商品短标题不能超过 200 字' }]}>
            <Input showCount maxLength={200} placeholder="用于商品卡片的一句话卖点，例如：枣香清甜，汤感醇厚" />
          </Form.Item>
          <Form.Item
            name="description"
            label="商品简介"
            extra="建议用 30–100 字说明茶类、产地、香气、滋味或适饮场景；保存后展示在小程序商品名称与价格之间。"
            rules={[{ max: 500, message: '商品简介不能超过 500 字' }]}
          >
            <TextArea
              rows={4}
              maxLength={500}
              showCount
              placeholder="例如：福鼎高山寿眉，枣香清甜、汤感醇和，适合日常品饮或节日送礼。"
            />
          </Form.Item>
          <Form.Item name="keywords" label="搜索关键词" extra="使用逗号分隔，供小程序搜索匹配，不直接展示给顾客。" rules={[{ max: 500 }]}>
            <Input placeholder="例如：寿眉,白茶,福鼎,礼盒,送礼,枣香" />
          </Form.Item>
          <Form.Item
            label="商品图片"
            required
            extra="最多 8 张，第一张作为商品封面；可一次选择多张图片批量上传，用箭头调整展示顺序"
          >
            <Space wrap align="start">
              {productImages.map((image, index) => (
                <div key={`${image}-${index}`} style={{ position: 'relative', width: 104 }}>
                  <Image
                    width={96}
                    height={96}
                    src={getImageUrl(image)}
                    style={{ objectFit: 'cover', borderRadius: 8 }}
                  />
                  <div style={{ textAlign: 'center', marginTop: 4 }}>
                    {index === 0 ? <Tag color="green">封面</Tag> : null}
                    <Button size="small" disabled={index === 0} onClick={() => moveImage(index, -1)}>←</Button>
                    <Button size="small" disabled={index === productImages.length - 1} onClick={() => moveImage(index, 1)}>→</Button>
                    <Button size="small" danger onClick={() => setProductImages(images => images.filter((_, i) => i !== index))}>删除</Button>
                  </div>
                </div>
              ))}
              {productImages.length < 8 ? (
                <Upload
                  accept="image/*"
                  multiple
                  showUploadList={false}
                  beforeUpload={handleBeforeUpload}
                >
                  <Button icon={<UploadOutlined />} loading={uploadingImage} style={{ height: 96 }}>
                    {uploadingImage ? '上传中…' : '添加图片（可多选）'}
                  </Button>
                </Upload>
              ) : null}
            </Space>
          </Form.Item>
          <Form.Item
            label="商品详情介绍图"
            extra="展示在商品详情页「数量」与「评价」之间；可上传一张长图或多张图片，不限宽高比与张数，按顺序纵向展示"
          >
            <Space wrap align="start">
              {productDetailImages.map((image, index) => (
                <div key={`${image}-${index}`} style={{ position: 'relative', width: 104 }}>
                  <Image
                    width={96}
                    height={96}
                    src={getImageUrl(image)}
                    style={{ objectFit: 'cover', borderRadius: 8 }}
                  />
                  <div style={{ textAlign: 'center', marginTop: 4 }}>
                    <Button size="small" disabled={index === 0} onClick={() => moveDetailImage(index, -1)}>←</Button>
                    <Button size="small" disabled={index === productDetailImages.length - 1} onClick={() => moveDetailImage(index, 1)}>→</Button>
                    <Button size="small" danger onClick={() => setProductDetailImages(images => images.filter((_, i) => i !== index))}>删除</Button>
                  </div>
                </div>
              ))}
              <Upload
                accept="image/*"
                multiple
                showUploadList={false}
                beforeUpload={handleDetailBeforeUpload}
              >
                <Button icon={<UploadOutlined />} loading={uploadingDetailImage} style={{ height: 96 }}>
                  {uploadingDetailImage ? '上传中…' : '添加详情图（可多选）'}
                </Button>
              </Upload>
            </Space>
          </Form.Item>
          <Space style={{ width: '100%', marginTop: 16 }} size="large">
            <Form.Item
              name="category_id"
              label="商品分类"
              rules={[{ required: true, message: '请选择商品分类' }]}
            >
              <Select
                placeholder="请选择商品分类"
                options={categories.map(item => ({ label: item.name, value: item.id }))}
                onChange={(value) => {
                  form.setFieldValue('subcategory_id', undefined);
                  loadSubcategories(value);
                }}
                style={{ width: 220 }}
              />
            </Form.Item>
            <Form.Item name="subcategory_id" label="二级分类">
              <Select
                allowClear
                placeholder={subcategories.length ? '请选择二级分类' : '该分类暂无二级分类'}
                disabled={!subcategories.length}
                options={subcategories.map(item => ({ label: item.name, value: item.id }))}
                style={{ width: 220 }}
              />
            </Form.Item>
          </Space>
          <Row gutter={16}>
            <Col span={6}><Form.Item name="price" label="售价" rules={[{ required: true }]}><InputNumber min={0} precision={2} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="original_price" label="原价"><InputNumber min={0} precision={2} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="cost_price" label="成本价" extra="仅后台可见"><InputNumber min={0} precision={2} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="stock" label="库存"><InputNumber min={0} precision={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="weight_text" label="净含量"><Input style={{ width: '100%' }} placeholder="例如：80克（10克*8泡）" /></Form.Item></Col>
            <Col span={12}><Form.Item name="unit" label="销售单位"><Select allowClear options={['件', '盒', '罐', '袋', '饼', '提', '斤', '套'].map(value => ({ label: value, value }))} /></Form.Item></Col>
          </Row>
          <Space size="large">
            <Form.Item name="status" label="上架" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="is_new" label="新品" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="is_hot" label="热门" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="is_recommend" label="推荐" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* 批量导入弹窗 */}
      <Modal
        title="批量导入商品"
        open={importVisible}
        onCancel={() => { setImportVisible(false); setImportResult(null); }}
        footer={null}
        width={560}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <p style={{ marginBottom: 8 }}>
              1. 下载模板，按模板格式填写商品信息（第一行是表头，勿修改）
            </p>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
              下载导入模板
            </Button>
          </div>
          <div>
            <p style={{ marginBottom: 8 }}>
              2. 上传填写好的 Excel 文件（支持 .xlsx / .xls，单行失败不影响其他行）
            </p>
            <Upload accept=".xlsx,.xls" showUploadList={false} customRequest={handleImport}>
              <Button type="primary" icon={<ImportOutlined />} loading={importing}>
                选择文件并导入
              </Button>
            </Upload>
          </div>
          {importResult && (
            <Card size="small" style={{ background: '#f6ffed' }}>
              <Row gutter={[16, 16]}>
                <Col span={8}><Statistic title="总行数" value={importResult.total || 0} /></Col>
                <Col span={8}><Statistic title="成功" value={importResult.success || 0} valueStyle={{ color: '#52c41a' }} /></Col>
                <Col span={8}><Statistic title="失败" value={(importResult.failed || []).length} valueStyle={{ color: '#ff4d4f' }} /></Col>
              </Row>
              {importResult.failed && importResult.failed.length > 0 && (
                <Table
                  style={{ marginTop: 12 }}
                  rowKey={(item, index) => index}
                  size="small"
                  pagination={false}
                  scroll={{ y: 180 }}
                  dataSource={importResult.failed}
                  columns={[
                    { title: '行号', dataIndex: 'row', width: 60 },
                    { title: '失败原因', dataIndex: 'message' },
                  ]}
                />
              )}
            </Card>
          )}
        </Space>
      </Modal>
      </Card>
    </div>
  );
};

export default ProductList;
